/**
 * Address history input (single entry) — Alpine component.
 * Moved from the inline script in components/address-history-input.blade.php:
 * inline blade scripts die when the component first appears via a Livewire
 * morph (morphdom doesn't execute injected script tags), which left the whole
 * field dead — no autocomplete AND no way to save manual input.
 *
 * Manual entry binds unconditionally in init(); Google Places is a progressive
 * enhancement layered on top when the central loader resolves. Every keystroke
 * persists the raw string into `street` so a typed address survives Continue
 * even if the user never presses Enter or selects a suggestion — with or
 * without Google.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';
import { unitField } from './unit-field';

export default function addressHistoryInput(modelValue, previousAddresses = []) {
    return {
        autocomplete: null,
        showParsedFields: false,
        suggestions: previousAddresses || [],
        street: '',
        apt: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States',
        fromDate: '',
        toDate: '',
        isCurrent: false,
        modelValue: modelValue,

        // Apt/Ste/Flr asked as a choice plus a number, stored back into the
        // same key as one canonical string -- the payload shape is unchanged.
        ...unitField('apt'),

        init() {
            this.bindManualEntry();

            loadGoogleMaps()
                .then(() => this.initAutocomplete())
                .catch(() => {}); // silent fallback — manual entry already works

            this.parseExistingValue();
            this.watchUnit();
            this.$watch('modelValue', () => this.parseExistingValue());
        },

        /** Always-on manual path: never depends on Google. */
        bindManualEntry() {
            const input = this.$refs.addressSearchInput;
            if (!input || input.dataset.manualBound === '1') return;
            input.dataset.manualBound = '1';

            // Persist raw text on every keystroke so nothing is lost if the
            // user taps Continue without Enter/blur/selection.
            input.addEventListener('input', () => {
                if (!this.showParsedFields) {
                    this.street = input.value.trim();
                    this.updateValue();
                }
            });

            // Handle Enter key for manual entry
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = input.value.trim();
                    if (value) {
                        this.street = value;
                        this.showParsedFields = true;
                        this.updateValue();
                    }
                }
            });

            // Delay lets a click on a Places suggestion win: place_changed sets
            // showParsedFields before this timeout inspects it.
            input.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.showParsedFields && input.value.trim()) {
                        this.street = input.value.trim();
                        this.showParsedFields = true;
                        this.updateValue();
                    }
                }, 250);
            });
        },

        initAutocomplete() {
            const input = this.$refs.addressSearchInput;
            if (!input || input.dataset.pacBound === '1') return;
            input.dataset.pacBound = '1';

            this.autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ["address_components", "formatted_address"],
                types: ["address"]
            });

            this.autocomplete.addListener("place_changed", () => {
                const place = this.autocomplete.getPlace();

                if (place.address_components) {
                    this.parseAddressComponents(place);
                    this.showParsedFields = true;
                    this.updateValue();
                    input.blur();
                }
            });
        },

        parseAddressComponents(place) {

            this.street = '';
            this.apt = '';
            this.city = '';
            this.state = '';
            this.zip = '';
            this.country = 'United States';

            // Shared parser: country-aware city/street fallbacks (UK
            // postal_town, sublocality wards, …) — see google-place-parser.
            const p = alvvaParsePlace(place);
            this.city = p.city;
            this.state = p.state;
            this.country = p.country || 'United States';
            this.zip = p.zip;
            this.street = p.street;
        },

        useSuggestion(suggestion) {
            this.street = suggestion.street || '';
            this.apt = suggestion.apt || suggestion.address2 || '';
            this.city = suggestion.city || '';
            this.state = suggestion.state || '';
            this.zip = suggestion.zip || '';
            this.country = suggestion.country || 'United States';
            this.showParsedFields = true;
            this.updateValue();
        },

        parseExistingValue() {
            // Don't overwrite a field the user is actively editing — the
            // modelValue watcher fires on our own keystrokes too, which clobbered
            // fast typing (chars dropped / value flashed).
            if (this.$el && this.$el.contains(document.activeElement)) return;
            const value = this.modelValue || '';
            if (!value) {
                this.resetFields();
                return;
            }

            try {
                const data = JSON.parse(value);
                this.street = data.street || '';
                this.apt = data.apt || '';
                this.city = data.city || '';
                this.state = data.state || '';
                this.zip = data.zip || '';
                this.country = data.country || 'United States';
                this.fromDate = data.from_date || '';
                this.toDate = data.to_date === 'Present' ? '' : (data.to_date || '');
                this.isCurrent = data.is_current || data.to_date === 'Present';

                // Show parsed fields if we have address data
                if (this.street || this.city) {
                    this.showParsedFields = true;
                }
            } catch (e) {
                this.resetFields();
            }
        },

        resetFields() {
            this.street = '';
            this.apt = '';
            this.city = '';
            this.state = '';
            this.zip = '';
            this.country = 'United States';
            this.fromDate = '';
            this.toDate = '';
            this.isCurrent = false;
            this.showParsedFields = false;
        },

        resetToSearch() {
            this.street = '';
            this.apt = '';
            this.city = '';
            this.state = '';
            this.zip = '';
            this.country = 'United States';
            this.showParsedFields = false;

            this.$nextTick(() => {
                if (this.$refs.addressSearchInput) {
                    this.$refs.addressSearchInput.value = '';
                    this.$refs.addressSearchInput.focus();
                }
            });

            this.updateValue();
        },

        isUS() {
            return !this.country || this.country === 'United States' || this.country === 'US' || this.country === 'USA';
        },

        handleCountryChange() {
            // Clear state if switching away from US (US state codes aren't valid elsewhere)
            const usStates = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','MP'];
            if (!this.isUS() && usStates.includes(this.state)) {
                this.state = '';
            }
            this.updateValue();
        },

        formatPostalCode() {
            if (this.isUS()) {
                this.zip = this.zip.replace(/[^\d-]/g, '').substring(0, 10);
            } else {
                this.zip = this.zip.replace(/[^\w\s-]/g, '').substring(0, 10);
            }
        },

        handleCurrentToggle() {
            if (this.isCurrent) {
                this.toDate = '';
            }
            this.updateValue();
        },

        getFormattedAddress() {
            let parts = [];
            if (this.street) {
                let line1 = this.street;
                if (this.apt) line1 += ', ' + this.apt;
                parts.push(line1);
            }
            let cityStateZip = [];
            if (this.city) cityStateZip.push(this.city);
            if (this.state) cityStateZip.push(this.state);
            if (this.zip) cityStateZip.push(this.zip);
            if (cityStateZip.length) parts.push(cityStateZip.join(', '));
            if (this.country && this.country !== 'United States') parts.push(this.country);
            return parts.join('\n');
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        },

        updateValue() {
            if (!this.street && !this.city) {
                this.modelValue = '';
                if (this.$refs.hiddenInput) {
                    this.$refs.hiddenInput.value = '';
                    this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
                return;
            }

            const data = {
                street: this.street,
                apt: this.apt,
                city: this.city,
                state: this.state,
                zip: this.zip,
                country: this.country,
                from_date: this.fromDate,
                to_date: this.isCurrent ? 'Present' : this.toDate,
                is_current: this.isCurrent,
                formatted_address: this.getFormattedAddress(),
                date_range: this.fromDate ? (this.formatDate(this.fromDate) + ' - ' + (this.isCurrent ? 'Present' : this.formatDate(this.toDate))) : ''
            };

            const value = JSON.stringify(data);
            this.modelValue = value;
            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = value;
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }
}
